import React, { useState } from 'react';
import { todayIso } from '../../utils/accountFilters';
import {
  X,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  ShieldAlert,
  Clock,
  ArrowRight,
  Copy,
  Check,
  Zap,
  Database,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { PTPDigestResult, PTPRecord } from '../../types';
import { ScaleSupportLogo } from './ScaleSupportLogo';

interface PTPDigestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToAccountsWithFilter?: (filterStatus: string) => void;
}

export const PTPDigestModal: React.FC<PTPDigestModalProps> = ({
  isOpen,
  onClose,
  onNavigateToAccountsWithFilter,
}) => {
  const { ptps, runPTPDigest, getExpiredPTPPreview, currentUser } = useSRMS();
  const [referenceDate, setReferenceDate] = useState<string>(todayIso());
  const [isProcessing, setIsProcessing] = useState(false);
  const [digestResult, setDigestResult] = useState<PTPDigestResult | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const preview = getExpiredPTPPreview(referenceDate);
  const totalPtps = ptps.length;
  const achievedPtps = ptps.filter((p) => p.status === 'Achieved');
  const alreadyBrokenPtps = ptps.filter((p) => p.status === 'Broken');

  const formatINR = (val: number) => `₹${val.toLocaleString('en-IN')}`;

  const handleRunDigest = async () => {
    setIsProcessing(true);
    try {
      const result = await runPTPDigest(referenceDate);
      setDigestResult(result);
    } catch (err) {
      console.error('PTP Digest error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyReport = () => {
    const lines = [
      `=========================================`,
      `SRMS PTP DIGEST REPORT - ${referenceDate}`,
      `Generated by: ${currentUser.name} (${currentUser.role})`,
      `=========================================`,
      `Total PTPs Evaluated: ${totalPtps}`,
      `Expired PTPs Marked Broken: ${digestResult ? digestResult.expiredCount : preview.expiredCount}`,
      `Expired Amount: ${formatINR(digestResult ? digestResult.expiredAmount : preview.expiredAmount)}`,
      `Due Today (Actionable): ${preview.dueTodayCount} (${formatINR(preview.dueTodayPTPs.reduce((s, p) => s + p.amount, 0))})`,
      `Upcoming Pipeline: ${preview.upcomingCount}`,
      `Achieved Recoveries: ${achievedPtps.length}`,
      `Already Broken: ${alreadyBrokenPtps.length}`,
      `Cloud Firestore Synchronized: ${digestResult?.sheetsSynced ? 'YES (Collections Updated)' : 'Active'}`,
      `Timestamp: ${new Date().toLocaleString()}`,
      `=========================================`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="bg-white/95 px-2.5 py-1 rounded-lg">
              <ScaleSupportLogo variant="compact" size="sm" showTagline={false} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black tracking-tight text-white flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span>PTP Digest &amp; Firestore Real-Time Resolver</span>
                </h2>
                <span className="text-[10px] font-mono px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full border border-blue-400/30">
                  Firestore Auto-Sync
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Evaluate expired Promise-to-Pay commitments, mark broken statuses, and synchronize Firestore collections
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition cursor-pointer"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-slate-800">
          {/* Reference Date & Evaluation Settings Bar */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 text-blue-700 rounded-xl">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900">Digest Reference Date</div>
                <p className="text-[11px] text-slate-500">
                  PTP commitments scheduled prior to this date with 'Pending' status are flagged as expired.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={referenceDate}
                onChange={(e) => {
                  setReferenceDate(e.target.value);
                  setDigestResult(null); // reset result to recalculate preview
                }}
                className="bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono font-bold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-xs"
              />
              <button
                onClick={() => {
                  setReferenceDate(todayIso());
                  setDigestResult(null);
                }}
                className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-medium rounded-lg border border-slate-200 shadow-xs transition cursor-pointer"
              >
                Today (23-Aug)
              </button>
            </div>
          </div>

          {/* 4 Status KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* 1. Expired (Target) */}
            <div
              className={`p-4 rounded-xl border transition ${
                preview.expiredCount > 0
                  ? 'bg-red-50/80 border-red-200 text-red-950 ring-1 ring-red-200'
                  : 'bg-slate-50 border-slate-200 text-slate-700'
              }`}
            >
              <div className="flex items-center justify-between text-[11px] font-bold text-red-700 uppercase tracking-wider">
                <span>Expired / Overdue</span>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div className="mt-2 text-2xl font-black text-red-700">{preview.expiredCount}</div>
              <div className="text-xs font-mono font-semibold text-red-800">
                {formatINR(preview.expiredAmount)}
              </div>
              <div className="text-[10px] text-red-600/80 mt-1">Pending past {referenceDate}</div>
            </div>

            {/* 2. Due Today */}
            <div className="p-4 rounded-xl border bg-amber-50/80 border-amber-200 text-amber-950">
              <div className="flex items-center justify-between text-[11px] font-bold text-amber-700 uppercase tracking-wider">
                <span>Due Today</span>
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div className="mt-2 text-2xl font-black text-amber-700">{preview.dueTodayCount}</div>
              <div className="text-xs font-mono font-semibold text-amber-800">
                {formatINR(preview.dueTodayPTPs.reduce((s, p) => s + p.amount, 0))}
              </div>
              <div className="text-[10px] text-amber-700/80 mt-1">Actionable on {referenceDate}</div>
            </div>

            {/* 3. Upcoming */}
            <div className="p-4 rounded-xl border bg-blue-50/80 border-blue-200 text-blue-950">
              <div className="flex items-center justify-between text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                <span>Upcoming</span>
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div className="mt-2 text-2xl font-black text-blue-700">{preview.upcomingCount}</div>
              <div className="text-xs font-mono font-semibold text-blue-800">
                {formatINR(
                  ptps
                    .filter((p) => p.status === 'Pending' && p.ptpDate > referenceDate)
                    .reduce((s, p) => s + p.amount, 0)
                )}
              </div>
              <div className="text-[10px] text-blue-700/80 mt-1">Future commitments</div>
            </div>

            {/* 4. Achieved & Resolved */}
            <div className="p-4 rounded-xl border bg-emerald-50/80 border-emerald-200 text-emerald-950">
              <div className="flex items-center justify-between text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                <span>Achieved</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="mt-2 text-2xl font-black text-emerald-700">{achievedPtps.length}</div>
              <div className="text-xs font-mono font-semibold text-emerald-800">
                {formatINR(achievedPtps.reduce((s, p) => s + p.amount, 0))}
              </div>
              <div className="text-[10px] text-emerald-700/80 mt-1">
                {alreadyBrokenPtps.length} already broken
              </div>
            </div>
          </div>

          {/* Success Banner if Digest was just run */}
          {digestResult && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-950 space-y-2 animate-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span className="font-bold text-sm text-emerald-900">
                    PTP Digest Batch Execution Completed
                  </span>
                </div>
                <span className="text-[11px] font-mono bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full font-bold border border-emerald-300">
                  {digestResult.timestamp}
                </span>
              </div>
              <p className="text-xs text-emerald-800 leading-relaxed">{digestResult.message}</p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[11px] font-semibold text-emerald-800 bg-white/80 px-2.5 py-1 rounded-lg border border-emerald-200 flex items-center gap-1.5">
                  <Database className="w-3.5 h-3.5 text-emerald-700" />
                  <span>Cloud Firestore Sync: Active</span>
                </span>
                <span className="text-[11px] font-semibold text-emerald-800 bg-white/80 px-2.5 py-1 rounded-lg border border-emerald-200">
                  ptps &amp; accounts Collections Updated
                </span>
              </div>
            </div>
          )}

          {/* Expired Commitments Detail Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-600" />
                  <span>
                    Expired Commitments to be Marked 'Broken' ({preview.expiredCount})
                  </span>
                </h3>
                <p className="text-xs text-slate-500">
                  These commitments missed the promised date without a confirmed collection receipt.
                </p>
              </div>

              <button
                onClick={handleCopyReport}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg border border-slate-200 flex items-center gap-1.5 transition cursor-pointer"
                title="Copy Summary Report"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Copied to Clipboard' : 'Copy Report'}</span>
              </button>
            </div>

            {preview.expiredPTPs.length === 0 ? (
              <div className="p-8 text-center bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                <div className="text-xs font-bold text-slate-800">
                  No Expired PTP Commitments
                </div>
                <p className="text-[11px] text-slate-500 max-w-md mx-auto">
                  All pending PTP commitments are scheduled on or after {referenceDate}. You can safely run
                  the digest to synchronize Cloud Firestore or inspect upcoming commitments.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-xs">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase">
                    <tr>
                      <th className="p-3">PTP ID</th>
                      <th className="p-3">Account &amp; Customer</th>
                      <th className="p-3">Promised Date</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Mode</th>
                      <th className="p-3">Agent</th>
                      <th className="p-3">Status Transition</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {preview.expiredPTPs.map((ptp: PTPRecord) => (
                      <tr key={ptp.id} className="hover:bg-red-50/40 transition">
                        <td className="p-3 font-mono font-bold text-blue-600">{ptp.id}</td>
                        <td className="p-3">
                          <div className="font-bold text-slate-900">{ptp.customerName}</div>
                          <div className="text-[11px] font-mono text-slate-400">{ptp.accountId}</div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded font-mono font-semibold text-[11px] border border-red-200">
                            {ptp.ptpDate}
                          </span>
                        </td>
                        <td className="p-3 font-mono font-bold text-red-700">{formatINR(ptp.amount)}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 text-[10px] font-semibold">
                            {ptp.ptpMode}
                          </span>
                        </td>
                        <td className="p-3 text-slate-600">
                          <div>{ptp.agentName}</div>
                          <div className="text-[10px] font-mono text-slate-400">{ptp.agentId}</div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1.5 font-semibold text-[11px]">
                            <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                              Pending
                            </span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200 font-bold">
                              Broken
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Due Today & Upcoming Quick List */}
          {preview.dueTodayCount > 0 && (
            <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span>Actionable Due Today: {preview.dueTodayCount} Commitments</span>
                </span>
                <span className="text-xs font-mono font-bold text-amber-900">
                  {formatINR(preview.dueTodayPTPs.reduce((s, p) => s + p.amount, 0))}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {preview.dueTodayPTPs.map((ptp) => (
                  <div
                    key={ptp.id}
                    className="p-2.5 bg-white rounded-lg border border-amber-200 flex items-center justify-between"
                  >
                    <div>
                      <div className="font-bold text-slate-900">{ptp.customerName}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {ptp.accountId} • Agent: {ptp.agentName}
                      </div>
                    </div>
                    <span className="font-mono font-bold text-amber-700">{formatINR(ptp.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-blue-600" />
            <span>Cloud Firestore: Live Database Synced</span>
          </div>

          <div className="flex items-center gap-3">
            {digestResult && onNavigateToAccountsWithFilter && (
              <button
                onClick={() => {
                  onClose();
                  onNavigateToAccountsWithFilter('PTP Broken');
                }}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl border border-slate-300 flex items-center gap-1.5 transition cursor-pointer"
              >
                <span>View Broken Accounts</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={onClose}
              className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-xl border border-slate-300 transition cursor-pointer shadow-xs"
            >
              Close
            </button>

            <button
              onClick={handleRunDigest}
              disabled={isProcessing}
              className={`px-5 py-2 rounded-xl text-xs font-black flex items-center gap-2 shadow-md transition cursor-pointer ${
                isProcessing
                  ? 'bg-blue-400 text-white cursor-wait'
                  : preview.expiredCount > 0
                  ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-red-500/20'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20'
              }`}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Processing PTP Digest &amp; Syncing Firestore...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 text-amber-300 fill-amber-300" />
                  <span>
                    {preview.expiredCount > 0
                      ? `Mark ${preview.expiredCount} Expired as Broken & Sync Firestore`
                      : 'Run PTP Digest & Sync Firestore'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
